import {
    Component,
    OnInit,
    Input,
    Output,
    ViewChild,
    ChangeDetectorRef,
    EventEmitter
} from '@angular/core';
import { ReplaySubject, combineLatest } from 'rxjs';
import { takeWhile, take } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { SelectItem, SortEvent } from '../../../shared/efaact-primeng.module';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import {
    IndirectRateRule,
    indirectRateRulesFeatureKey
} from '../../store/indirect-rate-rule';
import { Table } from '../../../shared/efaact-primeng.module';
import { IStatusMessage, ResponseStatus } from '../../../shared';
import { IShowErrors } from '../../../shared/errors-sidenav';

@Component({
    selector: 'ef-indirect-rate-rule',
    templateUrl: './indirect-rate-rule.component.html',
    styleUrls: ['./indirect-rate-rule.component.scss'],
    standalone: false
})
export class IndirectRateRuleComponent implements OnInit {
    dropdownItemHeight = environment.dropdownItemHeight;
    rowGroupMetadata: any;
    private _rates: SelectItem[];
    @Input() public set rates(value: SelectItem[]) {
        this._rates = value;
        setTimeout(() => {
            this.selectedRate = 'GAR';
            this.getRules(this.selectedRate, this.selectedCostCenter);
            this.log.verbose({
                type: 'IndirectRateRuleComponent:rates',
                value,
                selectedRate: this.selectedRate
            });
        }, 0);
    }
    public get rates(): SelectItem[] {
        return this._rates;
    }
    private _costCenters: SelectItem[];
    @Input() public set costCenters(value: SelectItem[]) {
        this._costCenters = value;
        setTimeout(() => {
            this.selectedCostCenter = value[0].value;
            this.getRules(this.selectedRate, this.selectedCostCenter);
            this.log.verbose({
                type: 'IndirectRateRuleComponent:costCenters',
                selectedRate: this.selectedRate
            });
        }, 0);
    }
    public get costCenters(): SelectItem[] {
        return this._costCenters;
    }
    selectedRate: string;
    selectedCostCenter: number;
    private _items: IndirectRateRule[];
    public get items(): IndirectRateRule[] {
        return this._items;
    }
    @Input() public set items(value: IndirectRateRule[]) {
        this._items = value;
        this.updateRowGroupMetaData(value);
    }
    @Output() SelectRate = new ReplaySubject<string>(1);
    @Output() SelectCostCenter = new ReplaySubject<number>(1);
    @ViewChild('dtIndirectRateStructure', { static: true })
    dtIndirectRateStructure: Table;
    private _selectedError: IStatusMessage;
    @Input() public set selectedError(value: IStatusMessage) {
        if (!value) {
            return;
        }
        if (this._selectedError && this._selectedError.MsgEntityID) {
            const oldRow = document.getElementById(
                `row-${this._selectedError.MsgEntityID.toString()}`
            );
            if (oldRow) {
                oldRow.classList.remove('highlight');
                oldRow.classList.remove('highlight-warning');
                oldRow.classList.remove('highlight-danger');
            }
        }
        this._selectedError = value;
        if (!(value && value.MsgEntityID)) {
            return;
        }
        const row = document.getElementById(
            `row-${value.MsgEntityID.toString()}`
        );
        this.log.verbose({
            type: 'PostingAccountComponent:selectedError',
            value,
            row
        });
        if (!row) {
            return;
        }
        row.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
        });
        row.classList.add('highlight');
        if (value.StatusCode === ResponseStatus.Warning) {
            row.classList.add('highlight-warning');
        }
        if (value.StatusCode === ResponseStatus.Failed) {
            row.classList.add('highlight-danger');
        }
    }
    public get selectedError(): IStatusMessage {
        return this._selectedError;
    }
    @Output() public ShowError = new EventEmitter<IShowErrors>();
    constructor(
        private log: LoggingService,
        private cdRef: ChangeDetectorRef
    ) {}

    ngOnInit() {
        combineLatest([this.SelectRate, this.SelectCostCenter])
            .pipe(take(1))
            .subscribe(() => {
                $('#divIndirectRate').show();
            });
    }
    getRules(rateType: string, costCenter: number) {
        this.log.verbose({
            type: 'IndirectRateRuleComponent:getRules',
            rateType,
            costCenter
        });

        this.SelectRate.next(rateType);
        this.SelectCostCenter.next(costCenter);
    }
    updateRowGroupMetaData(data: IndirectRateRule[]) {
        this.log.verbose({
            type: 'IndirectRateRuleComponent:updateRowGroupMetaData',
            data
        });
        this.rowGroupMetadata = {};
        if (data) {
            for (let i = 0; i < data.length; i++) {
                const rowData = data[i];
                const category = rowData.Pool_Base;
                if (i === 0) {
                    this.rowGroupMetadata[category] = { index: 0, size: 1 };
                } else {
                    const previousRowData = data[i - 1];
                    const previousRowGroup = previousRowData.Pool_Base;
                    if (category === previousRowGroup) {
                        this.rowGroupMetadata[category].size++;
                    } else {
                        this.rowGroupMetadata[category] = { index: i, size: 1 };
                    }
                }
            }
            this.log.verbose({
                type: 'IndirectRateRuleComponent:updateRowGroupMetaData',
                data,
                rowGroupMetadata: this.rowGroupMetadata
            });
            this.cdRef.markForCheck();
        }
    }
    onStatusClick(item: IndirectRateRule) {
        this.log.verbose({
            type: 'IndirectRateRuleComponent:onStatusClick',
            item
        });
        this.ShowError.emit({ open: true, MsgEntityID: item.id });
    }
    customSort($event: SortEvent) {
        this.log.verbose({
            type: 'IndirectRateRuleComponent:customSort',
            $event
        });
        $event.data.sort((a: IndirectRateRule, b: IndirectRateRule) => {
            const result = a.Pool_Base.toLocaleLowerCase().localeCompare(
                b.Pool_Base.toLocaleLowerCase()
            );
            return (
                (result === 0
                    ? a[$event.field]
                          .toLocaleLowerCase()
                          .localeCompare(b[$event.field].toLocaleLowerCase())
                    : result) * $event.order
            );
        });
    }
    onFilter($event) {
        this.log.verbose({
            type: 'IndirectRateRuleComponent:onFilter',
            $event
        });
        // setTimeout(() => {
        this.updateRowGroupMetaData($event.filteredValue);
        // }, 50);
    }
    onSort($event) {
        this.log.verbose({
            type: 'IndirectRateRuleComponent:onSort',
            $event
        });
        setTimeout(() => {
            this.updateRowGroupMetaData(
                this.dtIndirectRateStructure.filteredValue ||
                    this.dtIndirectRateStructure.value
            );
        }, 50);
    }
}
